import { ReactNode, useEffect } from "react";
import type { InsightsPreviewMode } from "../../features/user/types";
import { Navigate, useLocation, useParams } from "react-router-dom";
import {
  INSIGHTS_CLASSIC_PATH,
  INSIGHTS_PREVIEW_PATH,
  insightsPreviewPreference,
  insightsRouteLocation,
  optIntoInsightsPreview,
  optOutOfInsightsPreview,
  type InsightsPreviewPreference,
  resolveInsightsRoute,
  useInsightsPreviewMode,
} from "./insights-preview-rollout";
import { InsightsPreviewOptOutProvider } from "./insights-preview-opt-out-context";

type InsightsPreviewRouteProps = {
  children: ReactNode;
  preference?: InsightsPreviewPreference;
};

type InsightsRouteContentProps = InsightsPreviewRouteProps & {
  mode: InsightsPreviewMode;
};

function InsightsRouteLoading() {
  return (
    <div
      className="lazy-route-fallback flex min-h-[320px] items-center justify-center"
      role="status"
      aria-label="Loading Insights"
    >
      <i className="fa-regular fa-spinner-third animate-spin" aria-hidden />
    </div>
  );
}

function InsightsRolloutBanner({
  action,
  message,
  onClick,
}: {
  action: string;
  message: string;
  onClick: () => void;
}) {
  return (
    <aside
      aria-label="New Insights available"
      className="flex h-8 shrink-0 items-center justify-center bg-blue-37 px-2 shadow-sm"
    >
      <button
        type="button"
        className="group flex h-full items-center gap-2 !p-0 font-inter text-sm leading-5 text-neutral-800 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-500"
        onClick={onClick}
      >
        <i
          className="fa-regular fa-bullhorn inline-flex size-5 items-center justify-center text-base leading-none text-blue-500"
          aria-hidden="true"
        />
        <span className="font-medium">
          {message}{" "}
          <span className="font-semibold text-blue-500 group-hover:underline">
            <span>{action}</span>
            <span aria-hidden="true"> →</span>
          </span>
        </span>
      </button>
    </aside>
  );
}

function matchingPath(base: string, splat: string) {
  return splat ? `${base}/${splat}` : base;
}

export function InsightsPreviewRouteContent({
  children,
  mode,
  preference = insightsPreviewPreference,
}: InsightsRouteContentProps) {
  const location = useLocation();
  const splat = useParams()["*"] ?? "";
  const isShareLink = splat === "share" || splat.startsWith("share/");

  useEffect(() => {
    if (mode === "rollout" && !isShareLink) preference.optIn();
  }, [isShareLink, mode, preference]);

  if (isShareLink) {
    return (
      <Navigate
        replace
        to={insightsRouteLocation(
          matchingPath(INSIGHTS_CLASSIC_PATH, splat),
          location
        )}
      />
    );
  }

  if (mode !== "rollout") return <>{children}</>;

  const switchToClassic = () => {
    window.location.assign(
      optOutOfInsightsPreview(window.location, preference)
    );
  };

  return (
    <InsightsPreviewOptOutProvider value={switchToClassic}>
      {children}
    </InsightsPreviewOptOutProvider>
  );
}

export function InsightsPreviewRoute(props: InsightsPreviewRouteProps) {
  const mode = useInsightsPreviewMode();
  return mode ? (
    <InsightsPreviewRouteContent {...props} mode={mode} />
  ) : (
    <InsightsRouteLoading />
  );
}

export function InsightsV2LegacyRouteContent({
  children,
  mode,
  preference = insightsPreviewPreference,
}: InsightsRouteContentProps) {
  const location = useLocation();
  const splat = useParams()["*"] ?? "";
  const isShareLink = splat === "share" || splat.startsWith("share/");
  const resolvedRoute = resolveInsightsRoute(
    mode,
    "classic",
    preference.isOptedIn()
  );

  if (resolvedRoute === "preview" && !isShareLink) {
    return (
      <Navigate
        replace
        to={insightsRouteLocation(
          matchingPath(INSIGHTS_PREVIEW_PATH, splat),
          location
        )}
      />
    );
  }

  if (mode !== "rollout" || isShareLink) return <>{children}</>;

  const switchToPreview = () => {
    // The two Insights experiences are separate bundles. Reload across the
    // boundary so the classic app's global CSS cannot leak into the preview.
    window.location.assign(
      optIntoInsightsPreview(
        matchingPath(INSIGHTS_PREVIEW_PATH, splat),
        location,
        preference
      )
    );
  };

  return (
    <div className="insights-route-shell">
      <InsightsRolloutBanner
        action="Try it now"
        message="The new Insights experience is available!"
        onClick={switchToPreview}
      />
      <div className="insights-route-content">{children}</div>
    </div>
  );
}

export function InsightsV2LegacyRoute(props: InsightsPreviewRouteProps) {
  const mode = useInsightsPreviewMode();
  return mode ? (
    <InsightsV2LegacyRouteContent {...props} mode={mode} />
  ) : (
    <InsightsRouteLoading />
  );
}
